import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Public guest portal (/g/:token) is a no-account, guest-facing route — never bootstrap auth here.
    if (window.location.pathname.startsWith("/g/")) {
      setUser(false);
      setLoading(false);
      return;
    }
    // No stored token → an anonymous visitor (e.g. the public landing). Skip /auth/me entirely so
    // we don't fire a guaranteed-401 request and clutter the console.
    if (!localStorage.getItem("si_token")) {
      setUser(false);
      setLoading(false);
      return;
    }
    loadUser();
  }, [loadUser]);

  const login = async (email, password, portal) => {
    const { data } = await api.post("/auth/login", { email, password, ...(portal ? { portal } : {}) });
    localStorage.setItem("si_token", data.token);
    // Hydrate the full user from /auth/me so DB-derived flags (payment_profile_complete,
    // subscription_active, legal_pending, …) are present immediately — the thin login
    // payload omits them, which otherwise re-triggers the payment-profile gate every login.
    await loadUser();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("si_token", data.token);
    try { track("signup_complete"); } catch (_) {}
    await loadUser();
    return data.user;
  };

  const logout = async () => {
    try {
      if (localStorage.getItem("si_token_real")) await api.post("/sales/demo/end");
    } catch (e) { console.error("Demo cleanup failed", e); }
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed", e); }
    localStorage.removeItem("si_token");
    localStorage.removeItem("si_token_real");
    localStorage.removeItem("si_business");
    setUser(false);
  };

  // Multi-business: switch the active business/location. Sets the X-Business-Id used by
  // every API call, then re-hydrates the user so restaurant_id + all data follow.
  const switchBusiness = async (businessId) => {
    if (businessId) localStorage.setItem("si_business", businessId);
    else localStorage.removeItem("si_business");
    await loadUser();
  };

  const enterDemo = async (token) => {
    localStorage.setItem("si_token_real", localStorage.getItem("si_token") || "");
    localStorage.setItem("si_token", token);
    await loadUser();
  };

  // Public customer demo: no prior session. Store the ephemeral demo-owner token and
  // flag it so the UI shows a customer-facing (not sales) demo banner + conversion CTA.
  const startPublicDemo = async () => {
    const lang = localStorage.getItem("si_lang") || "en";
    const { data } = await api.post(`/public/demo/start?language=${encodeURIComponent(lang)}`);
    localStorage.setItem("si_token", data.token);
    localStorage.setItem("si_public_demo", "1");
    // Confirm the ephemeral demo session is actually live before returning. Right after
    // provisioning there can be brief read-lag on /auth/me; if we returned with user=false the
    // caller would navigate into a Protected route, get bounced back to the landing page, and the
    // visitor would have to click "Skip to live demo" a second time. Poll a few times to prevent that.
    let me = await loadUser();
    for (let i = 0; i < 4 && !me; i++) {
      await new Promise((r) => setTimeout(r, 500));
      me = await loadUser();
    }
    if (!me) throw new Error("demo_session_unconfirmed");
    return data;
  };

  const exitPublicDemo = async () => {
    localStorage.removeItem("si_token");
    localStorage.removeItem("si_public_demo");
    setUser(false);
  };

  const startImpersonation = async ({ token, impersonation_id, target_name }) => {
    localStorage.setItem("si_imp_super", localStorage.getItem("si_token") || "");
    localStorage.setItem("si_imp_id", impersonation_id || "");
    localStorage.setItem("si_imp_name", target_name || "");
    localStorage.setItem("si_token", token);
    await loadUser();
  };

  const endImpersonation = async () => {
    const impId = localStorage.getItem("si_imp_id");
    try { await api.post("/auth/impersonate/end", { impersonation_id: impId }); }
    catch (e) { console.error("End impersonation failed", e); }
    const superTok = localStorage.getItem("si_imp_super");
    if (superTok) localStorage.setItem("si_token", superTok);
    else localStorage.removeItem("si_token");
    localStorage.removeItem("si_imp_super");
    localStorage.removeItem("si_imp_id");
    localStorage.removeItem("si_imp_name");
    await loadUser();
  };

  const exitDemo = async () => {
    try { await api.post("/sales/demo/end"); } catch (e) { console.error("Demo end failed", e); }
    const real = localStorage.getItem("si_token_real");
    if (real) localStorage.setItem("si_token", real);
    else localStorage.removeItem("si_token");
    localStorage.removeItem("si_token_real");
    await loadUser();
  };

  // Demo role-switching: swap the active token to another member of the same demo
  // restaurant (keeps the rep's real token so Exit Demo still works). Uses real auth.
  const viewAsDemo = async (userId) => {
    const { data } = await api.post("/sales/demo/view-as", { user_id: userId });
    localStorage.setItem("si_token", data.token);
    // Clear the previous demo user's cached language so it can never carry over; the new
    // profile's preference (or the business default) is applied on load.
    localStorage.removeItem("si_lang");
    await loadUser();
  };

  const resetDemo = async () => {
    const { data } = await api.post("/sales/demo/reset");
    localStorage.setItem("si_token", data.token);
    await loadUser();
  };

  // Platform admin "act as a live customer": enter the admin's own isolated test workspace
  // as its owner (keeps the super-admin token so Exit restores it).
  const enterTestWorkspace = async () => {
    const { data } = await api.post("/admin/test-workspace/enter");
    localStorage.setItem("si_ws_admin", localStorage.getItem("si_token") || "");
    localStorage.setItem("si_ws_name", data?.restaurant?.name || "Test Workspace");
    localStorage.setItem("si_token", data.token);
    localStorage.removeItem("si_lang");
    await loadUser();
    return data;
  };

  const exitTestWorkspace = async () => {
    const superTok = localStorage.getItem("si_ws_admin");
    if (superTok) localStorage.setItem("si_token", superTok);
    else localStorage.removeItem("si_token");
    localStorage.removeItem("si_ws_admin");
    localStorage.removeItem("si_ws_name");
    try { await api.post("/admin/test-workspace/exit"); } catch (e) { /* audit only */ }
    await loadUser();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, loadUser, switchBusiness, enterDemo, exitDemo, viewAsDemo, resetDemo, startImpersonation, endImpersonation, startPublicDemo, exitPublicDemo, enterTestWorkspace, exitTestWorkspace }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
